/**
 * Définisseur de taille — parcours en quatre questions, sans mètre ruban.
 *
 * Stature → poids → type de ventre → type de carrure → taille annoncée.
 *
 * ## Pourquoi aucune mesure n'est demandée
 *
 * Décision du principal, 2026-09-09, après essai : le bourrelet redouté ne se
 * forme pas, même une taille en dessous. Le risque qui justifiait le ruban
 * tombe, et l'étape « mesurez-vous » coûtait plus cher qu'elle ne rapportait —
 * personne n'a de mètre sur son canapé. Le parcours relevé chez Bellutia est
 * repris pour ça, et pour l'identification que donnent les silhouettes.
 *
 * ## La grille reste la source de vérité
 *
 * 🛑 Ce fichier ne contient AUCUNE mesure. Les fourchettes sont lues dans le
 * tableau de la page Shopify `guide-des-tailles`, déjà rendu dans la modale.
 * Corriger la grille depuis l'administration corrige donc aussi le définisseur,
 * et il n'existe nulle part deux jeux de chiffres à tenir d'accord.
 *
 * Les quatre réponses ne remplacent pas la grille, elles y ramènent :
 *
 *   1. stature et poids donnent l'IMC ;
 *   2. l'IMC donne un tour de ventre estimé, par `4 × IMC - 6` ;
 *   3. le type de ventre corrige l'estimation de ±6 cm, parce qu'à IMC égal la
 *      répartition change ;
 *   4. la carrure la corrige de ±3 cm. Moitié moins que le ventre : c'est le
 *      tour de taille qui commande, comme dans la FAQ de la fiche. Avant le
 *      2026-09-19, la carrure passait par une taille « poitrine » dont on
 *      gardait la plus grande, et « plus fine » ne changeait jamais rien ;
 *   5. l'estimation est placée dans la colonne tour de taille de la grille.
 *
 * ## Ce que la formule doit retrouver : la taille habituelle
 *
 * 🛑 La bonne taille est celle que l'homme porte d'habitude en vêtements. Essai
 * du principal, 2026-09-19 : taille habituelle M, le M maintient bien, le L pas
 * assez. La FAQ dit la même chose au client (« Prenez votre taille habituelle »).
 *
 * `4 × IMC - 6` retombe sur les tailles habituelles courantes — à 178 cm :
 * M vers 75-80 kg, L vers 85 kg, XL vers 90-95 kg, XXL vers 100 kg, 3XL dès
 * 107 kg environ. La 3XL (grille fournisseur, 2026-09-27) prolonge le même pas
 * de 10 cm et la formule n'a pas été retouchée : les seuils gardent un écart
 * régulier d'environ 8 kg par taille, à 175 cm comme à 190 cm.
 * Un repère médical tour de taille / IMC (94 cm à IMC 25, 102 cm à IMC 30) a été
 * essayé le 2026-09-19 puis écarté : il ramenait presque tout le monde en L,
 * soit une à deux tailles sous la taille habituelle des 90-100 kg.
 *
 * ⚠️ Au bas de la grille, la formule sous-estime : 178 cm et 70 kg sortent en S
 * quand ce gabarit porte souvent du M. Hors cœur de cible, laissé tel quel.
 *
 * ## Sélection de la taille
 *
 * Le bouton final clique le bouton radio natif, comme `default-variant.js` :
 * c'est la mécanique du thème qui met alors à jour le prix, les médias et le
 * bouton d'achat, sans qu'on la duplique. Ça vaut aussi sur les pages de packs,
 * dont le sélecteur de taille est le même contrôle.
 */

/** Décalage en centimètres appliqué à l'estimation, par silhouette déclarée. */
const SHAPE_OFFSETS = { slim: -6, mid: 0, wide: 6 };

/** Poids de la carrure face au ventre : ±3 cm au lieu de ±6. */
const BUILD_WEIGHT = 0.5;

/**
 * Lit les fourchettes du tableau de la page Shopify.
 *
 * @param {HTMLElement} root
 * @returns {{ size: string, waist: [number, number], chest: [number, number] }[]}
 */
function readGrid(root) {
  const rows = root.querySelectorAll('.size-guide__table tbody tr');
  const grid = [];

  for (const row of rows) {
    const size = row.querySelector('th')?.textContent?.trim();
    const cells = row.querySelectorAll('td');
    if (!size || cells.length < 2) continue;

    // « 76 – 85 cm » — le séparateur est un demi-cadratin, et l'unité est dans
    // la cellule. On ne retient que les nombres, dans leur ordre d'apparition.
    const range = (cell) => (cell.textContent?.match(/\d+/g) ?? []).map(Number);
    const waist = range(cells[0]);
    const chest = range(cells[1]);
    if (waist.length < 2 || chest.length < 2) continue;

    grid.push({ size, waist: [waist[0], waist[1]], chest: [chest[0], chest[1]] });
  }

  return grid;
}

/**
 * Rang de la première taille dont la borne haute couvre la mesure.
 *
 * 🛑 On teste la borne HAUTE seule, jamais l'appartenance à la fourchette. Les
 * fourchettes de la grille ne se touchent pas — 76-85 puis 86-95 — et une
 * estimation tombe régulièrement dans le trou : 85,4 cm n'appartenait à aucune
 * ligne et sortait donc au maximum. Un homme de 175 cm et 70 kg se voyait
 * annoncer du XXL.
 *
 * Prendre la borne haute referme les trous. L'estimation étant arrondie au
 * centimètre, elle ne tombe plus dans un trou d'un centimètre ; le test reste
 * pour une grille qui en aurait de plus larges. Il ne traduit pas la règle
 * client, qui est l'inverse : entre deux tailles, la plus petite.
 * Sous la première fourchette on obtient la plus petite taille, au-delà de la
 * dernière la plus grande.
 *
 * @param {[number, number][]} ranges
 * @param {number} value
 * @returns {number}
 */
function rankFor(ranges, value) {
  if (ranges.length === 0) return -1;

  for (let i = 0; i < ranges.length; i++) {
    if (value <= ranges[i][1]) return i;
  }

  return ranges.length - 1;
}

class SizeFinder extends HTMLElement {
  connectedCallback() {
    this.grid = readGrid(this);
    this.answers = { height: null, weight: null, belly: null, build: null };
    this.stepIndex = 0;

    this.steps = [...this.querySelectorAll('[data-step]')];
    this.resultPanel = this.querySelector('[data-step="result"]');

    this.addEventListener('click', this.handleClick);
    this.addEventListener('input', this.handleInput);
    this.addEventListener('change', this.confine);

    // Les sliders portent déjà leur valeur par défaut dans le balisage : on la
    // lit plutôt que de la fixer ici, pour que l'éditeur reste maître.
    for (const slider of this.querySelectorAll('input[type="range"][data-answer]')) {
      this.answers[slider.dataset.answer] = Number(slider.value);
      this.renderSlider(slider);
    }

    this.showStep(0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('input', this.handleInput);
    this.removeEventListener('change', this.confine);
  }

  /**
   * 🛑 Les événements du parcours ne sortent pas du composant.
   *
   * Le définisseur est rendu à l'intérieur de `variant-picker__form` — le picker
   * natif n'accepte pas d'enfants, donc le dialogue vit dans le fieldset des
   * tailles. Un `input` ou un `change` qui remonte jusqu'au formulaire est lu
   * par Horizon comme un choix de variante : il redemande la section au serveur
   * et remplace le bloc. Le définisseur revenait alors neuf, curseur ramené à sa
   * valeur par défaut, alors même que le déplacement avait été enregistré. Un
   * curseur qui saute en arrière à chaque geste est inutilisable.
   *
   * C'est le même piège que le `type="button"` de la croix, plus bas dans le
   * snippet : tout ce qui vit dans ce formulaire doit être confiné.
   */
  confine = (event) => {
    event.stopPropagation();
  };

  handleInput = (event) => {
    const slider = event.target;
    if (!(slider instanceof HTMLInputElement) || slider.type !== 'range') return;

    event.stopPropagation();

    this.answers[slider.dataset.answer] = Number(slider.value);
    this.renderSlider(slider);
  };

  /** Le chiffre géant et la piste remplie suivent le curseur. */
  renderSlider(slider) {
    const output = this.querySelector(`[data-output="${slider.dataset.answer}"]`);
    if (output) output.textContent = slider.value;

    const min = Number(slider.min);
    const max = Number(slider.max);
    const ratio = max > min ? (Number(slider.value) - min) / (max - min) : 0;
    slider.style.setProperty('--slider-fill', `${ratio * 100}%`);
  }

  handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const choice = target.closest('[data-choice]');
    if (choice instanceof HTMLElement) {
      this.choose(choice);
      return;
    }

    // Le visiteur venu chercher le tableau : on déplie la grille et on l'y
    // amène, sans lui faire traverser les quatre questions.
    if (target.closest('[data-action="grid"]')) {
      const grid = this.querySelector('[data-grid]');
      if (grid instanceof HTMLDetailsElement) {
        // La grille est retirée de la vue tant qu'elle n'est pas demandée : son
        // seul en-tête sous le parcours laissait croire à un second contenu, et
        // faisait du définisseur un préambule au tableau plutôt que le chemin.
        grid.hidden = false;
        grid.open = true;
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    if (target.closest('[data-action="next"]')) this.showStep(this.stepIndex + 1);
    if (target.closest('[data-action="back"]')) this.back();
    if (target.closest('[data-action="restart"]')) this.restart();
    if (target.closest('[data-action="apply"]')) this.applySize();
  };

  /**
   * Un cran en arrière. À la première question, un hôte qui l'autorise
   * (`data-exitable`, posé par le panneau de composition) reçoit
   * `size-finder:exit` et reprend la main. Sur la fiche, le bouton n'existe pas
   * à cette étape : la croix du dialogue en tient lieu.
   */
  back() {
    if (this.stepIndex === 0 && this.hasAttribute('data-exitable')) {
      this.dispatchEvent(new CustomEvent('size-finder:exit', { bubbles: true }));
      return;
    }

    this.showStep(this.stepIndex - 1);
  }

  /** Une carte de silhouette vaut réponse ET passage à l'étape suivante. */
  choose(button) {
    const group = button.dataset.answer;
    this.answers[group] = button.dataset.choice;

    for (const sibling of this.querySelectorAll(`[data-choice][data-answer="${group}"]`)) {
      sibling.setAttribute('aria-pressed', String(sibling === button));
    }

    this.showStep(this.stepIndex + 1);
  }

  showStep(index) {
    const bounded = Math.max(0, Math.min(index, this.steps.length - 1));
    const step = this.steps[bounded];
    if (!step) return;

    // Le résultat ne se calcule qu'une fois les quatre réponses données : y
    // arriver par le bouton « précédent » ne doit pas annoncer une taille.
    if (step === this.resultPanel) {
      if (!this.computeResult()) return;
    }

    this.stepIndex = bounded;

    for (const panel of this.steps) panel.hidden = panel !== step;

    this.renderProgress();

    // Le panneau défile beaucoup entre deux étapes de hauteurs différentes.
    // Sans ça, une question courte s'ouvre au milieu de la précédente.
    this.closest('dialog')?.scrollTo({ top: 0 });
  }

  renderProgress() {
    const total = this.steps.length - 1;
    const isResult = this.stepIndex === total;
    const position = this.querySelector('[data-progress-label]');
    const bar = this.querySelector('[data-progress-bar]');

    if (position) {
      position.textContent = isResult
        ? position.dataset.resultLabel
        : position.dataset.stepLabel
            .replace('{current}', String(this.stepIndex + 1))
            .replace('{total}', String(total));
    }

    if (bar) bar.style.setProperty('--progress', `${((this.stepIndex + 1) / (total + 1)) * 100}%`);

    const back = this.querySelector('[data-action="back"]');
    if (back instanceof HTMLElement) back.hidden = this.stepIndex === 0 && !this.hasAttribute('data-exitable');
  }

  /**
   * @returns {boolean} faux si une réponse manque ou si la grille est illisible.
   */
  computeResult() {
    const { height, weight, belly, build } = this.answers;
    if (!height || !weight || !belly || !build || this.grid.length === 0) return false;

    const bmi = weight / (height / 100) ** 2;
    const waist = Math.round(4 * bmi - 6 + SHAPE_OFFSETS[belly] + SHAPE_OFFSETS[build] * BUILD_WEIGHT);

    const row = this.grid[rankFor(this.grid.map((row) => row.waist), waist)];
    if (!row) return false;

    this.recommended = row.size;

    const output = this.querySelector('[data-result-size]');
    if (output) output.textContent = row.size;

    for (const slot of this.querySelectorAll('[data-recap]')) {
      slot.textContent = this.recapFor(slot.dataset.recap);
    }

    const apply = this.querySelector('[data-action="apply"]');
    if (apply instanceof HTMLElement) {
      apply.textContent = apply.dataset.label.replace('{size}', row.size);
    }

    this.renderAvailability(row.size);
    return true;
  }

  /** Reprise des réponses sous la taille annoncée : une recommandation qu'on
   *  peut vérifier est plus crédible qu'un oracle. */
  recapFor(key) {
    if (key === 'height') return `${this.answers.height} cm`;
    if (key === 'weight') return `${this.answers.weight} kg`;

    const group = key === 'belly' ? 'belly' : 'build';
    const chosen = this.querySelector(
      `[data-choice="${this.answers[group]}"][data-answer="${group}"]`
    );
    return chosen?.dataset.recapLabel ?? '';
  }

  radioFor(size) {
    const name = this.dataset.optionName;
    if (!name) return null;

    const inputs = this.ownerDocument.querySelectorAll(
      `input[type="radio"][data-option-name="${CSS.escape(name)}"]`
    );

    for (const input of inputs) {
      if (input instanceof HTMLInputElement && input.value === size) return input;
    }

    return null;
  }

  /** Une taille en rupture ne doit pas être proposée comme si elle partait
   *  aujourd'hui : le bouton le dit avant le clic, pas après. */
  renderAvailability(size) {
    const notice = this.querySelector('[data-soldout-notice]');
    const apply = this.querySelector('[data-action="apply"]');
    const input = this.radioFor(size);
    const soldOut = input != null && input.getAttribute('aria-disabled') === 'true';

    if (notice instanceof HTMLElement) notice.hidden = !soldOut;
    if (apply instanceof HTMLElement) apply.hidden = soldOut || input == null;
  }

  applySize() {
    // Un hôte qui garde la taille pour lui, le panneau de composition, annule
    // l'événement : la fiche n'est alors pas touchée et son dialogue reste
    // ouvert. Sur la fiche, personne ne l'annule.
    const claim = new CustomEvent('size-finder:apply', {
      detail: { size: this.recommended },
      bubbles: true,
      cancelable: true,
    });
    if (!this.dispatchEvent(claim)) return;

    const input = this.radioFor(this.recommended);
    if (!input || input.getAttribute('aria-disabled') === 'true') return;

    if (!input.checked) input.click();

    // On ferme et on s'arrête là. Faire défiler jusqu'au bouton d'achat se lit
    // comme une pression, et le client vient déjà de faire quatre choix.
    this.closest('dialog-component')?.closeDialog?.();
  }

  restart() {
    for (const button of this.querySelectorAll('[data-choice]')) {
      button.setAttribute('aria-pressed', 'false');
    }

    this.answers.belly = null;
    this.answers.build = null;
    this.showStep(0);
  }
}

if (!customElements.get('size-finder')) {
  customElements.define('size-finder', SizeFinder);
}
